import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import ClientShell from "@/components/ClientShell";
import { useCategories } from "@/hooks/useCategories";
import { ArrowLeft, PaperPlaneTilt, UploadSimple, X, FileText, Image as ImageIcon } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function NewQuotation() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { categories } = useCategories();
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadingName, setUploadingName] = useState("");
  const fileRef = React.useRef(null);

  const ALLOWED = [".pdf", ".jpg", ".jpeg", ".png", ".dwg", ".dxf"];
  const MAX_FILES = 5;

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const errors = [];
    const next = [...files];
    for (const f of picked) {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED.includes(ext)) {
        errors.push(`${f.name}: unsupported type`);
        continue;
      }
      if (next.length >= MAX_FILES) {
        errors.push(`Max ${MAX_FILES} files`);
        break;
      }
      if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    }
    if (errors.length) toast.error(errors[0]);
    setFiles(next);
  };

  const removeFile = (idx) => setFiles((fs) => fs.filter((_, i) => i !== idx));
  const isImage = (f) => /\.(jpg|jpeg|png)$/i.test(f.name);

  const selections = React.useMemo(() => {
    const s = {};
    if (params.get("sel_material")) s.Material = params.get("sel_material");
    if (params.get("sel_size")) s.Size = params.get("sel_size");
    if (params.get("sel_finish")) s.Finish = params.get("sel_finish");
    return s;
  }, [params]);

  const [form, setForm] = useState(() => {
    const noteLines = [];
    if (params.get("brand")) noteLines.push(`Brand: ${params.get("brand")}`);
    if (params.get("sel_material")) noteLines.push(`Material: ${params.get("sel_material")}`);
    if (params.get("sel_size")) noteLines.push(`Size: ${params.get("sel_size")}`);
    if (params.get("sel_finish")) noteLines.push(`Finish: ${params.get("sel_finish")}`);
    return {
      category: params.get("category") || "",
      material: params.get("product") || "",
      quantity: params.get("qty") || "",
      dimensions: params.get("sel_size") || "",
      project_name: "",
      notes: noteLines.join("\n"),
      specifications: {},
    };
  });

  React.useEffect(() => {
    if (!form.category && categories.length > 0) {
      setForm((f) => ({ ...f, category: categories[0].category_id }));
    }
  }, [categories, form.category]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.material.trim()) {
      toast.error("Please describe the material");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/quotations", { ...form, specifications: selections });
      const quotationId = res.data.quotation_id;

      // Upload site drawings & photos (sequential, with a progress bar).
      if (files.length > 0) {
        setUploading(true);
        for (let i = 0; i < files.length; i += 1) {
          const f = files[i];
          setUploadingName(f.name);
          const fd = new FormData();
          fd.append("file", f);
          try {
            await api.post(`/quotations/${quotationId}/attachments`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: (evt) => {
                const filePct = evt.total ? evt.loaded / evt.total : 0;
                setUploadPct(Math.round(((i + filePct) / files.length) * 100));
              },
            });
          } catch (upErr) {
            const d = upErr?.response?.data?.detail;
            toast.error(typeof d === "string" ? `${f.name}: ${d}` : `Couldn't upload ${f.name}`);
          }
        }
        setUploading(false);
      }

      toast.success(`Request ${res.data.quotation_number} submitted`);
      navigate(`/quotations/${quotationId}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to submit request");
      setUploading(false);
    }
    setBusy(false);
  };

  return (
    <ClientShell>
      <Link
        to="/quotations"
        data-testid="new-quotation-back"
        className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 mb-6"
      >
        <ArrowLeft size={14} /> Back to quotations
      </Link>

      <div className="mb-8">
        <div className="mt-tiny text-[#FF5A1F]">New request</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-2">
          Tell us what you need.
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-xl">
          Fill in your requirements. We'll review, run a quality check, and send you a quote with certificates.
        </p>
      </div>

      {Object.keys(selections).length > 0 && (
        <div
          className="max-w-2xl mb-4 bg-white border border-[#FF5A1F] p-4"
          data-testid="quotation-selection-summary"
        >
          <div className="mt-tiny text-[#FF5A1F] mb-2">Your selection</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(selections).map(([k, v]) => (
              <span
                key={k}
                data-testid={`selection-chip-${k.toLowerCase()}`}
                className="inline-flex items-center gap-1.5 border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-sm"
              >
                <span className="text-zinc-500 text-xs uppercase tracking-wide">{k}</span>
                <b className="text-zinc-900">{v}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={submit} className="bg-white border border-zinc-200 p-6 md:p-8 max-w-2xl space-y-5" data-testid="new-quotation-form">
        <div>
          <div className="mt-tiny text-zinc-500 mb-1.5">Category</div>
          <Select value={form.category} onValueChange={(v) => setField("category", v)}>
            <SelectTrigger className="h-11 rounded-none border-zinc-200" data-testid="quotation-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {categories.map((c) => (
                <SelectItem key={c.category_id} value={c.category_id}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="mt-tiny text-zinc-500 mb-1.5">Material *</div>
          <Input
            data-testid="quotation-material-input"
            value={form.material}
            onChange={(e) => setField("material", e.target.value)}
            placeholder="e.g. 18mm HMR board, teak veneer, 12mm tempered glass"
            className="h-11 rounded-none border-zinc-200"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="mt-tiny text-zinc-500 mb-1.5">Quantity</div>
            <Input
              data-testid="quotation-quantity-input"
              value={form.quantity}
              onChange={(e) => setField("quantity", e.target.value)}
              placeholder="e.g. 50 sheets, 200 sqft"
              className="h-11 rounded-none border-zinc-200"
            />
          </div>
          <div>
            <div className="mt-tiny text-zinc-500 mb-1.5">Dimensions</div>
            <Input
              data-testid="quotation-dimensions-input"
              value={form.dimensions}
              onChange={(e) => setField("dimensions", e.target.value)}
              placeholder="e.g. 2400 × 1200 × 18 mm"
              className="h-11 rounded-none border-zinc-200"
            />
          </div>
        </div>

        <div>
          <div className="mt-tiny text-zinc-500 mb-1.5">Project name</div>
          <Input
            data-testid="quotation-project-input"
            value={form.project_name}
            onChange={(e) => setField("project_name", e.target.value)}
            placeholder="e.g. Marina Tower — Phase 2"
            className="h-11 rounded-none border-zinc-200"
          />
        </div>

        <div>
          <div className="mt-tiny text-zinc-500 mb-1.5">Notes for our team</div>
          <Textarea
            data-testid="quotation-notes-input"
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
            placeholder="Finish, colour, certifications, timeline preferences..."
            className="min-h-[110px] rounded-none border-zinc-200"
          />
        </div>

        <div>
          <div className="mt-tiny text-zinc-500 mb-1.5">Site Drawings & Photos</div>
          <p className="text-xs text-zinc-500 mb-2">
            Upload up to {MAX_FILES} files — PDF, JPG, PNG or CAD (.dwg, .dxf). Snap photos on site or attach drawings.
          </p>
          <label
            data-testid="quotation-files-label"
            className="block border border-dashed border-zinc-300 hover:border-[#FF5A1F] hover:bg-orange-50/40 p-5 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors text-center"
          >
            <UploadSimple size={22} className="text-[#FF5A1F]" />
            <span className="text-sm font-medium text-zinc-700">Tap to add files or take a photo</span>
            <span className="text-xs text-zinc-400">{files.length}/{MAX_FILES} added</span>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf,image/*,application/pdf"
              onChange={onPickFiles}
              disabled={files.length >= MAX_FILES}
              className="hidden"
              data-testid="quotation-files-input"
            />
          </label>

          {files.length > 0 && (
            <div className="mt-3 grid gap-2" data-testid="quotation-files-list">
              {files.map((f, idx) => (
                <div
                  key={`${f.name}-${idx}`}
                  data-testid={`quotation-file-${idx}`}
                  className="flex items-center justify-between gap-3 border border-zinc-200 bg-white p-2.5"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {isImage(f) ? (
                      <ImageIcon size={18} className="text-[#0047AB] shrink-0" weight="duotone" />
                    ) : (
                      <FileText size={18} className="text-[#B22222] shrink-0" weight="duotone" />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-900 truncate">{f.name}</div>
                      <div className="text-xs text-zinc-400">{(f.size / 1024 / 1024).toFixed(2)} MB</div>
                    </div>
                  </div>
                  {!uploading && (
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      data-testid={`quotation-file-remove-${idx}`}
                      className="text-zinc-400 hover:text-[#B22222] p-1"
                      aria-label="Remove file"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {uploading && (
            <div className="mt-3" data-testid="quotation-upload-progress">
              <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                <span className="truncate">Uploading {uploadingName}…</span>
                <span>{uploadPct}%</span>
              </div>
              <div className="h-2 bg-zinc-100 overflow-hidden rounded-full">
                <div
                  className="h-full bg-[#FF5A1F] transition-all duration-200"
                  style={{ width: `${uploadPct}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={busy}
            data-testid="quotation-submit-button"
            className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04814] text-white px-6 py-3 font-medium transition-colors disabled:opacity-60"
          >
            <PaperPlaneTilt size={18} weight="fill" />{" "}
            {uploading ? "Uploading files..." : busy ? "Sending..." : "Submit request"}
          </button>
        </div>
      </form>
    </ClientShell>
  );
}
